import React from "react"
import { type RouteObject } from "react-router-dom"

import { PermissionGate } from "../../../../components/PermissionGate/PermissionGate.tsx"
import { LegacyFilesRedirect } from "../../../Project/ProjectViewPage/components/LegacyFilesRedirect.tsx"
import { ProjectDefaultSectionRedirect } from "../../../Project/ProjectViewPage/components/ProjectDefaultSectionRedirect.tsx"
import InvoicesAvailabilityGate from "../../../Invoices/components/InvoicesAvailabilityGate.tsx"
import { RoutesPaths } from "../../../../constants/routes.ts"
import ProjectAddPage from "../../../Project/ProjectAddPage/ProjectAddPage.tsx"
import ProjectEditPage from "../../../Project/ProjectEditPage/ProjectEditPage.tsx"
import ProjectViewLayout from "../../../Project/ProjectViewPage/components/ProjectViewLayout.tsx"
import ProjectViewAlbumSection from "../../../Project/ProjectViewPage/components/sections/ProjectViewAlbum/ProjectViewAlbumSection.tsx"
import ProjectViewChecklistsSection from "../../../Project/ProjectViewPage/components/sections/ProjectViewChecklists/ProjectViewChecklistsSection.tsx"
import ProjectViewCommissionsSection from "../../../Project/ProjectViewPage/components/sections/ProjectViewCommissions/ProjectViewCommissionsSection.tsx"
import ProjectViewFilesSection from "../../../Project/ProjectViewPage/components/sections/ProjectViewFiles/ProjectViewFilesSection.tsx"
import ProjectViewFinanceSection from "../../../Project/ProjectViewPage/components/sections/ProjectViewFinance/ProjectViewFinanceSection.tsx"
import ProjectViewInvoicesSection from "../../../Project/ProjectViewPage/components/sections/ProjectViewInvoices/ProjectViewInvoicesSection.tsx"
import ProjectViewLogsSection from "../../../Project/ProjectViewPage/components/sections/ProjectViewLogs/ProjectViewLogsSection.tsx"
import ProjectViewMilestonesSection from "../../../Project/ProjectViewPage/components/sections/ProjectViewMilestones/ProjectViewMilestonesSection.tsx"
import ProjectViewSubcontractorsSection from "../../../Project/ProjectViewPage/components/sections/ProjectViewSubcontractors/ProjectViewSubcontractorsSection.tsx"
import { ProjectsArchiveQueryRedirect } from "../../../Archive/LegacyArchiveRedirect.tsx"
import { ProjectsViewResolver } from "../../../Projects/components/ProjectsViewResolver.tsx"
import ProjectsListPage from "../../../Projects/ProjectsListPage/ProjectsListPage.tsx"

// import ProjectViewOverview from "../../../Project/ProjectViewPage/components/sections/ProjectViewOverview/ProjectViewOverview.tsx";

export const projectRoutes: RouteObject[] = [
  {
    path: RoutesPaths.project.list,
    element: (
      <PermissionGate pages={["projects"]}>
        <ProjectsArchiveQueryRedirect>
          <ProjectsListPage />
        </ProjectsArchiveQueryRedirect>
      </PermissionGate>
    )
  },
  {
    path: RoutesPaths.project.grid,
    element: (
      <PermissionGate pages={["projects"]}>
        <ProjectsViewResolver />
      </PermissionGate>
    )
  },
  {
    path: RoutesPaths.project.add,
    element: <ProjectAddPage />
  },
  {
    path: RoutesPaths.project.edit(":id"),
    element: (
      <PermissionGate
        paths={["projects.projects.project_edit"]}
        description={"You don't have permission to edit project."}
      >
        <ProjectEditPage />
      </PermissionGate>
    )
  },
  {
    path: RoutesPaths.project.view(":id")._layout,
    element: <ProjectViewLayout />,
    children: [
      {
        index: true,
        element: <ProjectDefaultSectionRedirect />
      },
      {
        path: RoutesPaths.project.view(":id").milestones,
        element: (
          <PermissionGate
            paths={[
              "projects.projects.project_view",
              "projects.milestones.project_milestone_view"
            ]}
          >
            <ProjectViewMilestonesSection />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.project.view(":id").commissions,
        element: (
          <PermissionGate
            paths={[
              "projects.projects.project_view",
              "projects.commissions.project_commission_view"
            ]}
          >
            <ProjectViewCommissionsSection />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.project.view(":id").finance,
        element: (
          <PermissionGate
            paths={[
              "projects.projects.project_view",
              "projects.finances.project_finance_view"
            ]}
          >
            {/* `min-h-fit`: this row's ancestors in ProjectViewLayout are
                `min-h-0`, which lets a flex item's automatic minimum size
                collapse to 0 instead of its content — so two full cards
                stacked here got silently squeezed short with no way to
                scroll to the clipped rows. An explicit `min-height` opts out
                of that automatic-minimum-size calculation entirely.

                No `overflow-auto`: with `min-h-fit` this div is always as
                tall as its content, so it never had overflow to detect and
                never scrolled — all the class did was make it a scrollport,
                which the Finance card's sticky add control then resolved
                against and so never floated. FixedContainer is the scroller. */}
            <div className={"space-y-6 min-h-fit"}>
              <ProjectViewFinanceSection />
              <ProjectViewSubcontractorsSection />
            </div>
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.project.view(":id").invoices,
        element: (
          <InvoicesAvailabilityGate>
            <PermissionGate
              paths={[
                "projects.projects.project_view",
                "projects.invoices.project_invoice_view"
              ]}
            >
              <ProjectViewInvoicesSection />
            </PermissionGate>
          </InvoicesAvailabilityGate>
        )
      },
      {
        path: RoutesPaths.project.view(":id").files(),
        element: (
          <PermissionGate
            paths={[
              "projects.projects.project_view",
              "projects.docs.project_doc_view"
            ]}
          >
            <ProjectViewFilesSection />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.project.view(":id").albums,
        element: <LegacyFilesRedirect />
      },
      {
        path: RoutesPaths.project.view(":id").legacyAlbum(":albumId"),
        element: <LegacyFilesRedirect />
      },
      {
        path: RoutesPaths.project.view(":id").documents,
        element: <LegacyFilesRedirect />
      },
      {
        path: RoutesPaths.project.view(":id").tasks,
        element: (
          <PermissionGate
            paths={[
              "projects.projects.project_view",
              "projects.checklists.project_checklist_view"
            ]}
          >
            <ProjectViewChecklistsSection />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.project.view(":id").logs,
        element: (
          <PermissionGate
            paths={[
              "projects.projects.project_view",
              "projects.features.project_activity_log"
            ]}
          >
            <ProjectViewLogsSection />
          </PermissionGate>
        )
      },
      {
        // The pre-merge "All Files" page only ever queried the album store,
        // never documents — a project with no album photos always rendered
        // "No files found" here, even with real documents/reviews pending.
        // Redirect old links into the current, unified Files tab instead of
        // fixing a page that duplicates it.
        path: RoutesPaths.project.view(":id").allFiles,
        element: <LegacyFilesRedirect />
      },
      {
        path: RoutesPaths.project.view(":id").album(":albumId"),
        element: (
          <PermissionGate
            paths={[
              "projects.projects.project_view",
              "projects.docs.project_doc_view"
            ]}
          >
            <ProjectViewAlbumSection />
          </PermissionGate>
        )
      }
    ]
  }
]
